import React, { useCallback, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Avatar from '../Avatar';
import FollowButton from './FollowButton';
import { useAuth } from '../../contexts/AuthContext';
import { useDataCache } from '../../contexts/DataCacheContext';
import { getPublicProfile } from '../../lib/database';
import { getBadgeByKey, RARITY } from '../../lib/badgeDefinitions';
import { totalXpForLevel, xpProgressInLevel } from '../../lib/xpUtils';
import BadgeDetailSheet from '../badges/BadgeDetailSheet';

function PublicProfilePage() {
  const { username } = useParams();
  const navigate = useNavigate();
  const { profile: me } = useAuth();

  const fetchProfile = useCallback(() => getPublicProfile(username), [username]);
  const { data: profile, loading } = useDataCache(
    username ? `public-profile-${username}` : null,
    fetchProfile,
    60000
  );

  const [followerCount, setFollowerCount] = useState(0);
  // Dettaglio badge (read-only): { def, earned_at } del badge tappato
  const [selectedBadge, setSelectedBadge] = useState(null);
  useEffect(() => {
    if (profile) setFollowerCount(profile.follower_count || 0);
  }, [profile]);

  if (loading) {
    return <CenteredMsg>Caricamento profilo…</CenteredMsg>;
  }

  // Privato o inesistente: la RPC ritorna null in entrambi i casi (privacy-first)
  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-8 text-center">
        <div className="text-5xl mb-4">🔒</div>
        <h1 className="text-lg font-bold text-gray-900">Profilo privato</h1>
        <p className="text-sm text-gray-500 mt-1">
          Questo profilo non è pubblico oppure non esiste.
        </p>
        <button
          type="button"
          onClick={() => navigate('/athlete/community')}
          className="mt-6 px-5 py-2.5 bg-primary-600 text-white font-semibold rounded-2xl text-sm"
        >
          Torna alla Community
        </button>
      </div>
    );
  }

  const level = profile.current_level || 1;
  const totalXp = profile.total_xp || 0;
  const progress = xpProgressInLevel(totalXp);
  const xpInLevel = totalXp - totalXpForLevel(level);
  const xpNeeded = totalXpForLevel(level + 1) - totalXpForLevel(level);
  const title = profile.selected_badge ? getBadgeByKey(profile.selected_badge) : null;
  const isSelf = me?.id === profile.id;
  const followingCount = profile.following_count || 0;

  const earnedBadges = (Array.isArray(profile.badges) ? profile.badges : [])
    .map((b) => ({ def: getBadgeByKey(b.badge_key), earned_at: b.earned_at }))
    .filter((b) => b.def);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 px-5 pt-12 pb-5">
        <button
          type="button"
          onClick={() => navigate('/athlete/community')}
          className="text-sm text-gray-500 mb-4 active:opacity-70"
        >
          ← Community
        </button>
        <div className="flex items-center gap-4">
          <Avatar name={profile.name} seed={profile.id} src={profile.avatar_url} size="w-16 h-16" textClass="text-2xl" />
          <div className="min-w-0 flex-1">
            <h1 className="text-xl font-bold text-gray-900 truncate">{profile.name || 'Atleta'}</h1>
            {profile.username && <p className="text-xs text-gray-400 truncate">@{profile.username}</p>}
            {title && (
              <p className="text-sm text-primary-600 font-semibold flex items-center gap-1 mt-0.5">
                <span>{title.icon}</span>
                <span className="truncate">{title.name}</span>
              </p>
            )}
          </div>
          {!isSelf && (
            <FollowButton
              targetId={profile.id}
              following={profile.is_following}
              onChange={(next) => setFollowerCount((c) => Math.max(0, c + (next ? 1 : -1)))}
              className="px-5 py-2"
            />
          )}
        </div>

        {profile.bio && <p className="text-sm text-gray-600 mt-3 whitespace-pre-line">{profile.bio}</p>}

        {/* Conteggi follower / following */}
        <div className="flex gap-6 mt-4">
          <div>
            <span className="text-base font-bold text-gray-900">{followerCount}</span>
            <span className="text-sm text-gray-500 ml-1">follower</span>
          </div>
          <div>
            <span className="text-base font-bold text-gray-900">{followingCount}</span>
            <span className="text-sm text-gray-500 ml-1">following</span>
          </div>
        </div>
      </header>

      <main className="px-4 py-5 space-y-6 max-w-lg mx-auto">
        {/* Livello & XP */}
        <section className="bg-white rounded-2xl p-5 shadow-sm space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center text-white text-lg font-bold shrink-0">
              {level}
            </div>
            <div>
              <p className="font-bold text-gray-900">Livello {level}</p>
              <p className="text-xs text-gray-500">{totalXp} XP totali</p>
            </div>
          </div>
          <div>
            <div className="flex justify-between text-xs text-gray-400 mb-1.5">
              <span>{xpInLevel} XP</span>
              <span>{xpNeeded} XP</span>
            </div>
            <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-primary-500 rounded-full" style={{ width: `${(progress * 100).toFixed(1)}%` }} />
            </div>
          </div>

          {/* Statistiche base pubbliche */}
          <div className="grid grid-cols-2 gap-3 pt-1">
            <StatPill label="Allenamenti" value={profile.total_workouts ?? 0} />
            <StatPill label="Badge" value={earnedBadges.length} />
          </div>
        </section>

        {/* Badge principali */}
        {earnedBadges.length > 0 && (
          <section className="space-y-3">
            <h2 className="text-base font-bold text-gray-800 px-1">Badge</h2>
            <div className="flex gap-3 overflow-x-auto pt-2 pb-2 px-4 -mx-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {earnedBadges.map(({ def, earned_at }) => {
                const rarity = RARITY[def.rarity] || RARITY.comune;
                // I badge SEGRETI di altri utenti restano mascherati e non
                // tappabili: il dettaglio ne rivelerebbe nome e criterio.
                if (def.secret) {
                  return (
                    <div key={def.key} className="shrink-0 w-20 flex flex-col items-center gap-1.5 text-center">
                      <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ring-1 bg-gradient-to-b ${rarity.glow} ${rarity.ring}`}>
                        <span className="text-3xl">🔮</span>
                      </div>
                      <span className="text-[11px] font-semibold text-gray-700 leading-tight line-clamp-2">{def.name}</span>
                    </div>
                  );
                }
                return (
                  <button
                    type="button"
                    key={def.key}
                    onClick={() => setSelectedBadge({ def, earned_at })}
                    aria-label={`Dettagli badge ${def.name}`}
                    className="shrink-0 w-20 flex flex-col items-center gap-1.5 text-center active:opacity-70"
                  >
                    <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ring-1 bg-gradient-to-b ${rarity.glow} ${rarity.ring}`}>
                      <span className="text-3xl">{def.icon}</span>
                    </div>
                    <span className="text-[11px] font-semibold text-gray-700 leading-tight line-clamp-2">{def.name}</span>
                  </button>
                );
              })}
            </div>
          </section>
        )}
      </main>

      {/* Dettaglio badge altrui: read-only (nome, descrizione, criterio, rarità) */}
      {selectedBadge && (
        <BadgeDetailSheet
          badge={selectedBadge.def}
          earned={true}
          earnedAt={selectedBadge.earned_at}
          readOnly
          onClose={() => setSelectedBadge(null)}
        />
      )}
    </div>
  );
}

function StatPill({ label, value }) {
  return (
    <div className="bg-gray-50 rounded-xl p-2.5 text-center">
      <p className="text-lg font-bold text-gray-900">{value}</p>
      <p className="text-xs text-gray-400 leading-tight mt-0.5">{label}</p>
    </div>
  );
}

function CenteredMsg({ children }) {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <p className="text-gray-400 text-sm">{children}</p>
    </div>
  );
}

export default PublicProfilePage;
